// Pure helpers for an event's race-day schedule. Copy of
// backend/src/lib/eventSchedule.ts (the backend has the unit tests) -- keep
// the two in step.

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** "2026-10-24" -> true. Rejects anything that isn't a real calendar date. */
export function isDayISO(value: unknown): value is string {
  if (typeof value !== "string" || !DAY.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

/** Every day from startDay to endDay inclusive ("YYYY-MM-DD" in, out).
 *  No end, or an end before the start, means a one-day event. Capped at 31
 *  days so a typo'd end year can't produce hundreds of empty days. */
export function eventDays(startDay: string, endDay?: string): string[] {
  if (!isDayISO(startDay)) return [];
  if (!endDay || !isDayISO(endDay) || endDay < startDay) return [startDay];
  const out: string[] = [];
  const d = new Date(`${startDay}T00:00:00Z`);
  while (out.length < 31) {
    const day = d.toISOString().slice(0, 10);
    out.push(day);
    if (day >= endDay) break;
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

export interface Schedulable {
  heatId: string;
  dayISO?: string;
  order?: number;
  roundType: "qualifier" | "main";
  roundNumber: number;
  createdAt: string;
}

/** The day a heat runs on. Heats saved before days existed (no dayISO), or
 *  pointing at a day that's no longer part of the event, show under the
 *  first day so nothing silently disappears. */
export function dayOf(heat: Pick<Schedulable, "dayISO">, days: string[]): string | undefined {
  if (heat.dayISO && days.includes(heat.dayISO)) return heat.dayISO;
  return days[0];
}

/** Run order within a day: explicit order first; rows without one (older
 *  heats) after, qualifiers before mains, then by round number, then by
 *  when they were added. */
export function compareRunOrder(a: Schedulable, b: Schedulable): number {
  const ao = a.order ?? Number.POSITIVE_INFINITY;
  const bo = b.order ?? Number.POSITIVE_INFINITY;
  if (ao !== bo) return ao < bo ? -1 : 1;
  if (a.roundType !== b.roundType) return a.roundType === "qualifier" ? -1 : 1;
  if (a.roundNumber !== b.roundNumber) return a.roundNumber - b.roundNumber;
  return a.createdAt.localeCompare(b.createdAt);
}

/** Heats bucketed by day, each day in run order. Every day is present,
 *  even with no heats yet. */
export function scheduleByDay<T extends Schedulable>(heats: T[], days: string[]): Map<string, T[]> {
  const out = new Map<string, T[]>(days.map((d) => [d, []]));
  for (const h of heats) {
    const day = dayOf(h, days);
    if (day) out.get(day)!.push(h);
  }
  for (const list of out.values()) list.sort(compareRunOrder);
  return out;
}

/** The order value for a heat appended to the end of a day. */
export function nextOrder(dayHeats: Pick<Schedulable, "order">[]): number {
  return dayHeats.reduce((max, h) => Math.max(max, h.order ?? 0), 0) + 1;
}

/** Moves one heat up or down within its day and renumbers the whole day
 *  1..n, returning only the heats whose order actually changed (the ones to
 *  save). Moving past either end is a no-op. */
export function moveInDay<T extends Schedulable>(
  dayHeats: T[],
  heatId: string,
  direction: "up" | "down",
): { heatId: string; order: number }[] {
  const list = [...dayHeats].sort(compareRunOrder);
  const i = list.findIndex((h) => h.heatId === heatId);
  const j = direction === "up" ? i - 1 : i + 1;
  if (i < 0 || j < 0 || j >= list.length) return [];
  [list[i], list[j]] = [list[j], list[i]];
  return list
    .map((h, idx) => ({ heatId: h.heatId, order: idx + 1, was: h.order }))
    .filter((h) => h.was !== h.order)
    .map(({ heatId: id, order }) => ({ heatId: id, order }));
}
